<script setup lang="ts">
import { computed, useSlots } from "vue";

export interface BadgeProps {
  /** A string representing the ARIA label for the badge. */
  ariaLabel?: string;
  /** A boolean representing if the content changes frequently or is static. */
  isDynamic?: boolean;
}

const { ariaLabel, isDynamic = false } = defineProps<BadgeProps>();

const ariaAttributes = computed(() => {
  if (ariaLabel) {
    return { "aria-hidden": "false", "aria-label": ariaLabel };
  }
  if (isDynamic) {
    return { "aria-live": "polite", role: "status" };
  }
  return { "aria-hidden": "true" };
});

const slotContentLength = computed(() => {
  const defaultSlot = useSlots().default?.();

  if (!defaultSlot || !defaultSlot[0]) {
    return 0;
  }

  const textContent = defaultSlot[0].children;
  return typeof textContent === "string" ? textContent.length : 0;
});
</script>

<template>
  <span
    class="inline-block bg-light-blue text-center text-xs/5 font-medium text-black"
    :class="{
      'h-4 w-4 rounded-full': slotContentLength === 0,
      'h-5 w-5 rounded-full': slotContentLength > 0 && slotContentLength < 3,
      'rounded-lg px-1': slotContentLength > 2,
    }"
    v-bind="ariaAttributes"
    data-testid="clr-badge"
  >
    <slot />
  </span>
</template>
